/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { styles } from './styles';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { LayoutChangeEvent, Linking, View } from 'react-native';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import { pd } from '@airasia-phoenix/react-native/tokens';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import ArrowDropDown from '@move-travel/react-native-phoenix-icons/ArrowDropDown';
import Download from '@move-travel/react-native-phoenix-icons/Download';
import PressableView from '@airasia-phoenix/react-native/components/PressableView';
import ButtonView, {
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView/index';
import { MenuModal } from '@airasia-rn/ui';
import { useTranslation } from 'react-i18next';
import { NavigationItem } from './types';
import { measureInViewport } from '../../utils';
import { getSelectedNavigationItemId } from '../../utils/selectedNavigationItem';

const NAV_ITEM_MARGIN = pd.ref.space.spacing20.px * 2;
const MORE_ITEM_MARGIN = pd.ref.space.spacing20.px;
const MORE_BUTTON_FALLBACK_WIDTH = 70;
const DEFAULT_APP_DOWNLOAD_ICON_URL =
  'https://static.airasia.com/mobile/phoenix/icons/v1/main/svg/file/file_download.svg';

const subscribeNoop = () => () => undefined;
export const NavigationBar = ({
  navigationItems = [],
  onItemPress,
}: {
  navigationItems?: NavigationItem[];
  onItemPress?: (item: NavigationItem) => void;
}) => {
  const { t } = useTranslation('commonWeb');
  // optimisticly rendering all item in desktop at first pass
  // in case of descrepency will auto adjust
  // True on the server and during hydration: the hydrating render must match
  // the server markup, so it must not read window.innerWidth.
  const isServerRender = useSyncExternalStore(
    subscribeNoop,
    () => false,
    () => true,
  );
  const isServerOrDesktop =
    isServerRender || typeof window === 'undefined' || window.innerWidth > 1200;
  const [containerWidth, setContainerWidth] = useState(
    isServerOrDesktop ? 750 : 0,
  );
  // Server-rendered items are already on screen; don't hide them after
  // hydration while they are being measured.
  const [startedVisible] = useState(isServerOrDesktop);
  const [visibleItems, setVisibleItems] = useState<NavigationItem[]>(
    isServerOrDesktop ? navigationItems : [],
  );
  const [remainingItems, setRemainingItems] = useState<NavigationItem[]>([]);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location) {
      setCurrentUrl(window.location.href);
    }
  }, []);

  const selectedId = useMemo(
    () =>
      currentUrl
        ? getSelectedNavigationItemId(navigationItems, currentUrl)
        : null,
    [currentUrl, navigationItems],
  );

  // Store each item’s measured width
  const itemWidths = useRef<{ [key: string]: number }>({});

  const recomputeFit = useCallback(() => {
    if (containerWidth <= 0 || navigationItems.length === 0) return;

    const slotWidth = (id: string) => {
      const w = itemWidths.current[id] ?? 0;
      if (w === 0) return 0;
      return w + (id === 'more' ? MORE_ITEM_MARGIN : NAV_ITEM_MARGIN);
    };

    const moreSlot = itemWidths.current['more']
      ? slotWidth('more')
      : MORE_BUTTON_FALLBACK_WIDTH;

    const naturalTotal = navigationItems.reduce(
      (sum, item) => sum + slotWidth(item.id),
      0,
    );
    const willNeedMore = naturalTotal > containerWidth;
    const budget = willNeedMore ? containerWidth - moreSlot : containerWidth;

    let used = 0;
    let stopShowing = false;
    const shown: NavigationItem[] = [];
    const hidden: NavigationItem[] = [];

    for (const item of navigationItems) {
      const w = slotWidth(item.id);
      const isPinned =
        item.type === 'app_link' ||
        item.type === 'custom' ||
        typeof item.label !== 'string';

      if (isPinned) {
        shown.push(item);
        used += w;
      } else if (!stopShowing && used + w <= budget) {
        shown.push(item);
        used += w;
      } else {
        stopShowing = true;
        hidden.push(item);
      }
    }

    setVisibleItems(shown);
    setRemainingItems(hidden);
  }, [containerWidth, navigationItems]);

  useEffect(() => {
    recomputeFit();
  }, [recomputeFit]);

  const [moreVisible, setMoreVisible] = useState(false);
  const [moreModalLayout, setMoreModalLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const moreButtonRef = useRef<React.ComponentRef<typeof View>>(null);

  const openMoreMenu = () => {
    measureInViewport(moreButtonRef.current, (layout) => {
      setMoreModalLayout(layout);
      setMoreVisible(true);
    });
  };

  const handleNavigationItemPress = (item: NavigationItem) => {
    if (item.id === 'more') {
      openMoreMenu();
      return;
    }

    onItemPress?.(item);
    Linking.openURL(item.redirectUrl);
  };

  const handleLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width - 50);
  };

  const handleItemLayout = (id: string, width: number) => {
    if (itemWidths.current[id] === width) return;
    itemWidths.current[id] = width;
    recomputeFit();
  };

  const handleModalClose = () => setMoreVisible(false);

  const moreButton = useMemo<NavigationItem>(() => {
    return {
      id: 'more',
      label: t('webHeaderView.navigation.more'),
      redirectUrl: '',
    };
  }, [t]);

  const renderTextNavigationItem = (label: string, itemId: string) => (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <TextView
        text={label}
        style={getTextStyle(
          itemId === selectedId
            ? pd.sys.typescale.sub_body_text_medium
            : pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_primary,
        )}
      />
      {itemId === 'more' && (
        <IconView
          height={24}
          width={24}
          tint={pd.sys.colour.content_secondary.hex}
        >
          <ArrowDropDown />
        </IconView>
      )}
    </View>
  );

  const renderNavigationItemContent = (item: NavigationItem) => {
    switch (item.type) {
      case 'app_link': {
        const isDefaultDownloadIcon =
          !item.imageUrl || item.imageUrl === DEFAULT_APP_DOWNLOAD_ICON_URL;

        return (
          <View pointerEvents="none">
            <ButtonView
              startIcon={
                isDefaultDownloadIcon
                  ? {
                      children: <Download />,
                      tint: pd.sys.colour.secondary.toString(),
                    }
                  : {
                      url: item.imageUrl,
                      tint: pd.sys.colour.secondary.toString(),
                    }
              }
              type={ButtonViewType.SECONDARY_SMALL}
              text={item.label}
              disabled={false}
              onPress={() => handleNavigationItemPress(item)}
            />
          </View>
        );
      }
      case 'text_link':
        return renderTextNavigationItem(item.label, item.id);
      case 'custom':
        return <View pointerEvents="box-none">{item.label}</View>;
      default: {
        const fallbackItem = item as NavigationItem;

        if (typeof fallbackItem.label === 'string') {
          return renderTextNavigationItem(fallbackItem.label, fallbackItem.id);
        }

        return <View pointerEvents="box-none">{fallbackItem.label}</View>;
      }
    }
  };

  // Hidden until every item has been measured and the fit is computed.
  const isReady =
    startedVisible ||
    (navigationItems.length > 0 &&
      navigationItems.some((item) => itemWidths.current[item.id] != null));

  const itemsToRender =
    remainingItems.length > 0 ? [...visibleItems, moreButton] : visibleItems;
  return (
    <View
      style={[styles.navigationBar, { opacity: isReady ? 1 : 0 }]}
      onLayout={handleLayout}
    >
      {itemsToRender.map((item, index) => (
        <View
          ref={item.id === 'more' ? moreButtonRef : undefined}
          key={item.id}
        >
          <PressableView
            tabIndex={
              item.type !== 'app_link' && typeof item.label === 'string'
                ? 0
                : -1
            }
            boxShadowBorderRadius={2}
            boxShadowSpread={4}
            onPress={() => handleNavigationItemPress(item)}
            style={item.id === 'more' ? styles.navItemLastItem : styles.navItem}
            onLayout={(e) =>
              handleItemLayout(item.id, e.nativeEvent.layout.width)
            }
          >
            {renderNavigationItemContent(item)}
          </PressableView>
        </View>
      ))}

      {/* Only render modal if we actually have hidden items */}
      {remainingItems.length > 0 && (
        <MenuModal
          visible={moreVisible}
          onClose={handleModalClose}
          containerWidth={178}
          listViewItems={remainingItems.map((item) => ({
            primaryText: item.label as string,
            onPress: () => {
              onItemPress?.(item);
              Linking.openURL(item.redirectUrl);
              handleModalClose();
            },
          }))}
          positionStyle={{
            position: 'absolute',
            top: (moreModalLayout?.y ?? 0) + (moreModalLayout?.height ?? 0) + 9,
            left:
              (moreModalLayout?.x ?? 0) + (moreModalLayout?.width ?? 0) - 178,
          }}
          testID="options"
        />
      )}
    </View>
  );
};
